/**
 * AuthGuard.tsx
 *
 * GLOBAL AUTHENTICATION GATE — App-Level Token Enforcement
 *
 * ARCHITECTURE:
 * - Wraps all routes at App.tsx level (similar to BrandingProvider)
 * - Checks for access_token on mount, navigation, and window focus
 * - Shows login overlay if no token (except for whitelisted public routes)
 * - Centralizes auth logic — no per-route wrappers needed
 *
 * PUBLIC ROUTES (No Auth Required):
 * - /login (badge reader)
 * - /qr/:qrCode (executive QR landing)
 * - /welcome (welcome center - public access)
 * - /skybridge (platform tram - public access)
 *
 * ALL OTHER ROUTES (Auth Required):
 * - /executive-access
 * - /component/:seq
 * - /{encoded_routes}
 *
 * TOKEN VALIDATION:
 * - Synchronous localStorage check (no API call, instant)
 * - Rechecks on window focus (detects expired tokens)
 * - Listens for storage events (detects logout in other tabs)
 *
 * VISIBILITY AUTH TIER INDEPENDENCE:
 * - Auth gate is MANDATORY regardless of database Visibility_Auth_Tier
 * - Even if tier=0 (public), user must still authenticate
 * - Database tiers control WHICH features are visible, not WHETHER to log in
 *
 * Created: 2026-06-07
 */
// @architect-approved: Routing utility wrapper, not a registered @tg-seq
// component — the "Self" component identity pattern does not apply here.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SecureStorage } from './SecureStorage';
import { resolveComponentBySeq } from './componentResolver';
import { isTokenExpired } from './jwtHelper';

// ReceptionLogin component (seq 10037) - compact login overlay
const ReceptionLogin = resolveComponentBySeq(10037);

interface AuthGuardProps {
    children: React.ReactNode;
}

/**
 * Public routes that don't require authentication
 * All other routes will trigger login overlay if no token exists
 */
const PUBLIC_ROUTES = [
    '/login',
    '/qr', // Executive QR landing pages (pattern: /qr/:qrCode)
    '/welcome', // Welcome center - public access
    '/skybridge', // Platform tram - public access
    '/component/10021', // Caboose marketing website (public)
    '/component/10022', // Techgacity marketing website (public)
];

/**
 * True only when the browser's hostname is the public marketing domain
 * (techgacity.com / www.techgacity.com). Mirrors App.tsx's isMarketingDomain()
 * so the root route ('/') is public on that domain without affecting the
 * portal domain (mytechgacity.com) or any other hostname.
 */
const isMarketingHostname = (): boolean => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return host === 'techgacity.com' || host === 'www.techgacity.com';
};

/**
 * Check if current path is a public route (no auth required)
 */
const isPublicRoute = (pathname: string): boolean => {
    if (pathname === '/' && isMarketingHostname()) return true;
    return PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
};

/**
 * AuthGuard — Global authentication gate for entire application
 *
 * Renders login overlay if:
 * - No access_token in localStorage
 * - User is NOT on a public route
 *
 * Validation triggers:
 * - Component mount
 * - Route navigation (pathname change)
 * - Window focus (tab becomes active)
 * - Storage events (logout in another tab)
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLoginOverlay, setShowLoginOverlay] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    /**
     * Check if route is a directory component (handles own auth)
     *
     * Directory patterns (nav_, dirm_, dirs_, gate_):
     * - Have visibility/activation auth tiers in database
     * - Use useDirectoryAuth hook for tier-based filtering
     * - gate_ pattern shows badge reader for authentication
     * - Allow loading without token (progressive auth model)
     */
    const isDirectoryRoute = (pathname: string): boolean => {
        const directoryPrefixes = ['nav_', 'dirm_', 'dirs_', 'gate_'];
        return directoryPrefixes.some((prefix) => {
            // Check both /prefix and /component/:seq patterns
            return pathname.includes(`/${prefix}`) || pathname.startsWith(`/${prefix}`);
        });
    };

    /**
     * Check if route is a marketing site (always public)
     *
     * Marketing patterns (mkt_):
     * - Public-facing marketing websites (Caboose, TechGacity, etc.)
     * - No authentication required to view
     * - Login overlay shown only when user clicks Login button
     */
    const isMarketingSite = (pathname: string): boolean => {
        return pathname.includes('/mkt_') || pathname.startsWith('/mkt_');
    };

    /**
     * Check if user has valid access token
     *
     * Validates:
     * - Token exists in localStorage
     * - Token is properly formatted (3 parts)
     * - Token is not expired (checks exp claim)
     *
     * Returns true if token is valid OR route is public OR directory
     */
    const checkAuth = useCallback(() => {
        const pathname = location.pathname;

        // Public routes bypass auth check
        if (isPublicRoute(pathname)) {
            console.log(`[AuthGuard] Public route ${pathname} — skipping auth check`);
            setShowLoginOverlay(false);
            setIsCheckingAuth(false);
            return true;
        }

        // Directory routes handle their own auth (useDirectoryAuth + gate pattern)
        if (isDirectoryRoute(pathname)) {
            console.log(
                `[AuthGuard] Directory route ${pathname} — allowing load (directory handles auth internally)`,
            );
            setShowLoginOverlay(false);
            setIsCheckingAuth(false);
            return true;
        }

        // Marketing sites are always public (no auth required)
        if (isMarketingSite(pathname)) {
            console.log(`[AuthGuard] Marketing site ${pathname} — public access allowed`);
            setShowLoginOverlay(false);
            setIsCheckingAuth(false);
            return true;
        }

        // Check for access token
        const accessToken = SecureStorage.getAccessToken();

        if (!accessToken) {
            console.warn(`[AuthGuard] No access token — showing login overlay for ${pathname}`);
            setShowLoginOverlay(true);
            setIsCheckingAuth(false);
            return false;
        }

        // Validate JWT structure and expiration
        try {
            if (isTokenExpired(accessToken)) {
                console.warn(
                    `[AuthGuard] Access token expired — showing login overlay for ${pathname}`,
                );
                // Clear expired token so apiService doesn't try to use it
                SecureStorage.clearTokens();
                setShowLoginOverlay(true);
                setIsCheckingAuth(false);
                return false;
            }
        } catch (error) {
            console.error(
                `[AuthGuard] Failed to validate token — showing login overlay for ${pathname}`,
                error,
            );
            // Clear malformed token
            SecureStorage.clearTokens();
            setShowLoginOverlay(true);
            setIsCheckingAuth(false);
            return false;
        }

        console.log(`[AuthGuard] Access token valid and not expired for ${pathname}`);
        setShowLoginOverlay(false);
        setIsCheckingAuth(false);
        return true;
    }, [location.pathname]);

    /**
     * Initial auth check on component mount
     */
    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    /**
     * Recheck auth on route navigation
     */
    useEffect(() => {
        console.log('[AuthGuard] Route changed — validating auth');
        checkAuth();
    }, [location.pathname, checkAuth]);

    /**
     * Recheck auth when window regains focus
     * Catches expired tokens while user was away
     */
    useEffect(() => {
        const handleFocus = () => {
            console.log('[AuthGuard] Window focus — validating auth');
            checkAuth();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [checkAuth]);

    /**
     * Listen for storage events (logout in another tab)
     */
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            // Only recheck if access_token changed
            if (e.key === 'access_token' || e.key === null) {
                console.log('[AuthGuard] Storage change detected — validating auth');
                checkAuth();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [checkAuth]);

    /**
     * Handle successful login from overlay
     */
    const handleLoginSuccess = () => {
        console.log('[AuthGuard] Login successful — closing overlay and rechecking auth');

        // Verify token exists before closing overlay
        const token = SecureStorage.getAccessToken();
        console.log('[AuthGuard] Token check in handleLoginSuccess:', !!token);

        if (!token) {
            console.error('[AuthGuard] ERROR: handleLoginSuccess called but no token found!');
            return;
        }

        setShowLoginOverlay(false);

        // Small delay to ensure React state updates complete, then recheck auth
        setTimeout(() => {
            console.log('[AuthGuard] Rechecking auth after overlay close');
            checkAuth();
        }, 50);
    };

    /**
     * Render login overlay if no token (for protected routes)
     */
    if (showLoginOverlay) {
        return <ReceptionLogin onLoginSuccess={handleLoginSuccess} returnTo={location.pathname} />;
    }

    /**
     * Render children (routes) if:
     * - Token exists, OR
     * - Route is public
     */
    return <>{children}</>;
};
